import { Button, Modal, Spinner, Typography } from '@volvo/vcdk-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';

type PollingStatus = 'idle' | 'polling' | 'success' | 'failure';

type Props = {
  isOpen: boolean;
  status: PollingStatus;
  onClose: () => void;
};

const MESSAGE_CYCLE_MS = 5000;

const WifiPollingModal: React.FC<Props> = ({ isOpen, status, onClose }) => {
  const { t } = useTranslation();
  const [messageIndex, setMessageIndex] = useState(0);

  const pollingMessages = [
    t(($) => $.components.modal.wifi_polling_modal.connecting),
    t(($) => $.components.modal.wifi_polling_modal.searching),
    t(($) => $.components.modal.wifi_polling_modal.almost),
    t(($) => $.components.modal.wifi_polling_modal.hang_tight),
    t(($) => $.components.modal.wifi_polling_modal.communicating),
    t(($) => $.components.modal.wifi_polling_modal.moment),
  ];

  useEffect(() => {
    if (status !== 'polling') return;

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % pollingMessages.length);
    }, MESSAGE_CYCLE_MS);

    return () => clearInterval(interval);
  }, [status, pollingMessages.length]);

  if (!isOpen) return null;

  const isError = status === 'failure';
  const title = isError
    ? t(($) => $.components.modal.wifi_polling_modal.title_error)
    : t(($) => $.components.modal.wifi_polling_modal.title);

  return createPortal(
    <Modal open={isOpen} onClose={isError ? onClose : undefined} maxWidth="90%" width={500}>
      <span slot="header">{title}</span>
      <div className="flex flex-col items-center gap-6 py-8">
        {status === 'polling' && (
          <>
            <Spinner size={48} />
            <Typography variant="body" className="text-center">
              {pollingMessages[messageIndex]}
            </Typography>
          </>
        )}
        {status === 'failure' && (
          <Typography variant="body" className="text-center">
            {t(($) => $.components.modal.wifi_polling_modal.error)}
          </Typography>
        )}
      </div>
      {isError && (
        <span slot="modalFooter">
          <Button variant="primary" onClick={onClose}>
            {t(($) => $.common.close)}
          </Button>
        </span>
      )}
    </Modal>,
    document.body
  );
};

export default withComponentErrorBoundary(WifiPollingModal);
